import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

// The admin console uses a SAME-ORIGIN axios instance so the httpOnly cookie
// is properly issued and sent — the Kubernetes ingress on this preview environment
// rewrites cross-origin CORS headers to `*`, which is incompatible with credentialed
// requests. The public storefront continues to use REACT_APP_BACKEND_URL.
const adminClient = axios.create({
  baseURL: "/api",
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Backwards-compat: clean up any legacy bearer token that earlier builds stored.
// Reading localStorage here is intentional and only purges old data — no
// sensitive data is now persisted client-side; the JWT lives in an httpOnly
// cookie issued by the backend.
const LEGACY_STORAGE_KEY = "pritch_admin_token_v1";

const AdminAuthContext = createContext(null);

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      if (localStorage.getItem(LEGACY_STORAGE_KEY)) {
        localStorage.removeItem(LEGACY_STORAGE_KEY);
      }
    } catch (err) {
      console.warn("[admin-auth] localStorage cleanup skipped", err);
    }
  }, []);

  const fetchMe = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminClient.get("/auth/me");
      setAdmin(data);
    } catch (err) {
      if (err?.response?.status && err.response.status !== 401) {
        console.warn("[admin-auth] /auth/me unexpected error", err);
      }
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await adminClient.post("/auth/login", { email, password });
    setAdmin(data.admin);
    return data.admin;
  }, []);

  const logout = useCallback(async () => {
    try {
      await adminClient.post("/auth/logout");
    } catch (err) {
      console.warn("[admin-auth] logout request failed", err);
    }
    setAdmin(null);
  }, []);

  const authClient = useCallback(() => adminClient, []);

  const value = useMemo(
    () => ({ admin, loading, login, logout, authClient }),
    [admin, loading, login, logout, authClient]
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
};

export const useAdminAuth = () => {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
};
